import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { setMeta } from "../../lib/seo";
import { Reveal } from "../../components/storefront/Reveal";

const CATEGORY_META = {
  "kadin-parfum": "Zarif ve büyüleyici kadın kokuları — çiçeksi, meyveli ve romantik notalar.",
  "erkek-parfum": "Güçlü ve karizmatik erkek kokuları — odunsu, baharatlı ve ferah notalar.",
  "gold-parfum": "Altın serinin gösterişli dünyası — lüksü sevenlere özel yoğun kalıcılık.",
  "niche-parfum": "Butik ve sanatsal üretim — sıradışı harmanlar, sınırlı bulunan özel kokular.",
  "legend-parfum": "Zamansız klasikler — efsaneleşmiş, ikonik ve unutulmaz imza kokular.",
  "oda-kokusu": "Evinize zarafet katın — bambu çubuklu difüzörler ve kalıcı oda kokuları.",
};

export default function DadyaParfumPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories/dadya-parfum/overview")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, []);

  const cat = data?.category;

  useEffect(() => {
    setMeta("description", cat?.seo_description || "Dadya Parfüm koleksiyonu: Kadın, Erkek, GOLD, Niche, Legend serileri ve oda kokuları.");
    if (cat?.seo_keywords) setMeta("keywords", cat.seo_keywords);
  }, [cat]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="dadya-landing">
      <Helmet>
        <title>{cat?.seo_title || "Dadya Parfüm | BIGELDİ"}</title>
        <link rel="canonical" href={`${window.location.origin}/dadya-parfum`} />
      </Helmet>

      <nav className="text-xs text-neutral-400 mb-6 flex items-center gap-2" data-testid="dadya-breadcrumb">
        <Link to="/" className="hover:text-amber-700">Anasayfa</Link><span>/</span>
        <span className="text-neutral-600">Dadya Parfüm</span>
      </nav>

      <div className="mb-12">
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-amber-700 mb-3">Marka Koleksiyonu</p>
        <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight" data-testid="dadya-title">Dadya Parfüm</h1>
        <p className="text-sm sm:text-base text-neutral-500 mt-4 max-w-2xl">
          {cat?.description || "Kadın, Erkek, GOLD, Niche ve Legend parfüm serileri ile bambu oda kokuları."}
        </p>
        {data && <p className="text-xs text-neutral-400 mt-2" data-testid="dadya-total">{data.total_products} ürün</p>}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#F4F2EB] rounded-3xl animate-pulse-soft" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
          {data?.children?.map((ch, i) => (
            <Reveal key={ch.category_id} delay={Math.min(i, 5) * 0.06}>
              <Link to={`/dadya-parfum/${ch.slug}`} className="group block" data-testid={`dadya-cat-card-${ch.slug}`}>
                <div className="relative aspect-[3/4] rounded-3xl overflow-hidden border border-[#E6E4DD] bg-gradient-to-br from-[#F4F2EB] via-[#FAF9F5] to-amber-100/60">
                  {ch.image_url || ch.banner_url ? (
                    <img src={ch.image_url || ch.banner_url} alt={ch.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-display font-black text-5xl sm:text-6xl text-amber-700/10 select-none">
                        {ch.name.split(" ")[0]}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[11px] font-semibold text-neutral-600">
                    {ch.product_count} ürün
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                    <h2 className="font-display font-extrabold text-xl sm:text-2xl text-white tracking-tight">{ch.name}</h2>
                    <p className="text-[13px] text-white/75 mt-1.5 leading-relaxed line-clamp-2">
                      {ch.description || CATEGORY_META[ch.slug] || ""}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-amber-600 text-white text-sm font-semibold group-hover:bg-amber-500 transition-colors">
                      Ürünleri İncele <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
